import TextareaInput from "../FormComponents/TextareaInput";
import SingleDateInput from "../FormComponents/SingleDateInput";
import Modal from "../common/Modal";
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { t } from "i18next";
import { get } from "lodash";
import addEditReminder from "./services/addEditReminder";
import Toast from "../Toast";
import classNames from "classnames";
import AwesomeIcon from "../AwesomeIcon";
import {
  faCheck,
  faChevronLeft,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import resolveReminder from "./services/resolveReminder";
import deletePatientReminder from "./services/deletePatientReminder";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { useAuth } from "@qure/qure-auth";
import { useStoreState } from "pullstate";
import Store from "../../utils/Store";
import Dropdown from "../common/Dropdown";
import SearchableCombobox from "../common/SearchableCombobox";
import { useWorkspacePermittedUsers } from "../WorkspaceManagement/services/useWorkspacePermittedUsers";
import useGetTaskLabels from "./services/useGetTaskLabels";
import getReminderNotes from "./services/getReminderNotes";
import createReminderNote from "./services/createReminderNote";
import TextInput from "../FormComponents/TextInput";
import Button from "../common/Button";
import { getFormattedDate } from "../../utils";
import { addDays, addMonths, addWeeks, addYears } from "date-fns";
import LinkPatientRecordsModal from "./LinkPatientRecordsModal";
import { DEFAULT_LINKED_RESOURCES } from "./constants";
import useGetLinkedFhirResources from "./services/useGetLinkedFhirResources";
import useGetLinkedReportDetails from "./services/useGetLinkedReportDetails";
import { AttachmentsView } from "./AttachmentsView";
import { LinkedRecordsPanel } from "./LinkedRecordsPanel";
import { compLettersBeforeNumbers } from "../../utils/string-compare.util";

const NOTES_PAGE_SIZE = 10;

/** DRF cursor pagination often returns `next` as a URL; GET expects a `cursor` query value. */
function normalizeNotesCursor(next) {
  if (next == null || next === "") return null;
  const s = typeof next === "string" ? next : String(next);
  try {
    const u =
      s.startsWith("http://") || s.startsWith("https://")
        ? new URL(s)
        : new URL(
            s,
            typeof window !== "undefined" ? window.location.origin : "http://localhost"
          );
    const c = u.searchParams.get("cursor");
    if (c != null && c !== "") return c;
  } catch {
    /* ignore */
  }
  return next;
}

const DUE_DATE_SHORTCUT_PILL_CLASS =
  "rounded-full border border-teal-600 bg-teal-50 px-3 py-1.5 text-[12px] font-medium text-teal-900 transition-colors " +
  "hover:border-teal-700 hover:bg-teal-600 hover:text-gray-100 " +
  "disabled:pointer-events-none disabled:opacity-40 " +
  "dark:border-teal-500/90 dark:bg-[#0C1723] dark:text-gray-100 dark:hover:border-teal-600 dark:hover:bg-teal-600 dark:hover:text-white";

/** Mantine DatePicker overrides for modal + long date format (same approach as PatientForm DOB). */
const REMINDER_DUE_DATE_PICKER_STYLES = {
  dropdown: {
    backgroundColor: "#F6F7F7",
    border: "1px solid #646B72",
    transition: "none !important",
    marginTop: 0,
    marginBottom: 6,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    display: "flex",
    justifyContent: "center",
    position: "absolute",
  },
  calendarHeader: {
    marginBottom: 6,
  },
  weekdayCell: {
    paddingBottom: 2,
  },
  day: {
    width: 30,
    height: 30,
    minHeight: 30,
    fontSize: 12,
    lineHeight: "28px",
    padding: 0,
    "&[data-selected]": {
      backgroundColor: "#00A9A7",
    },
    "&[data-in-range]": {
      backgroundColor: "#D9DBDD",
    },
    "&[data-first-in-range]": {
      backgroundColor: "#00A9A7",
    },
    "&[data-last-in-range]": {
      backgroundColor: "#00A9A7",
    },
  },
  cell: {
    borderTop: "none",
  },
};

/** Resolve the task type label shown in the modal header / reminders UI */
export function getTaskLabelDisplayName(reminder, taskLabelsCatalog = []) {
  const L = reminder?.labels?.[0];
  if (!L) return "";
  const direct = L.label ?? L.name;
  if (direct != null && String(direct).trim() !== "") return String(direct).trim();
  const id = L.value ?? L.id;
  if (id == null || !Array.isArray(taskLabelsCatalog) || !taskLabelsCatalog.length) {
    return "";
  }
  const found = taskLabelsCatalog.find(
    (x) => x != null && (x.id === id || String(x.id) === String(id))
  );
  return found?.name != null ? String(found.name).trim() : "";
}

function buildLabelIds(reminder) {
  const labels = reminder?.labels;
  if (!Array.isArray(labels)) return [];
  return labels.map((l) => l?.value ?? l?.id).filter((id) => id != null && id !== "");
}

function buildLinkedResourcesPayload(reminder) {
  const lr = reminder?.linked_resources;
  return {
    appointments: (lr?.appointments ?? []).map(String),
    encounters: (lr?.encounters ?? []).map(String),
    reports: (lr?.reports ?? []).map(String),
  };
}

export const CreateEditPatientReminder = ({
  selectedReminder,
  patient_uid,
  onCloseModal,
  refetchReminders,
  workspaceId,
}) => {
  const [reminder, setReminder] = useState(() => {
    if (!selectedReminder?.title && !!selectedReminder?.context) {
      const ctx = selectedReminder.context;
      return {
        ...selectedReminder,
        title: ctx.length > 50 ? ctx.slice(0, 47) + "..." : ctx,
      };
    }
    return selectedReminder;
  });
  const reminderRef = useRef(null);
  reminderRef.current = reminder;
  const [isReminderSubmitInProgress, setIsReminderSubmitInProgress] = useState(false);
  const [isAttachmentsViewOpen, setIsAttachmentsViewOpen] = useState(false);
  const [linkedResources, setLinkedResources] = useState(
    selectedReminder?.linked_resources
      ? buildLinkedResourcesPayload(selectedReminder)
      : DEFAULT_LINKED_RESOURCES
  );
  const [reminderNotes, setReminderNotes] = useState([]);
  const [notesNextCursor, setNotesNextCursor] = useState(null);
  const [notesHasNext, setNotesHasNext] = useState(false);
  const [isReminderNotesLoading, setIsReminderNotesLoading] = useState(false);
  const [isReminderNotesLoadingMore, setIsReminderNotesLoadingMore] = useState(false);
  const [isNoteComposerOpen, setIsNoteComposerOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isDeletingReminder, setIsDeletingReminder] = useState(false);
  const [isResolvingReminder, setIsResolvingReminder] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const notesPaginationRef = useRef({
    hasNext: false,
    nextCursor: null,
    initialLoading: false,
    loadingMore: false,
  });
  const reminderFormBodyRef = useRef(null);
  const tryLoadNextNotesRef = useRef(() => {});
  const [isLinkRecordsOpen, setIsLinkRecordsOpen] = useState(false);
  const { userInfo } = useAuth();
  const { isDarkMode } = useStoreState(Store, (s) => ({
    isDarkMode: s.isDarkMode,
  }));

  useEffect(() => {
    Store.update((s) => {
      s.isCreatePatientReminderFormOpen = true;
    });

    return () => {
      Store.update((s) => {
        s.isCreatePatientReminderFormOpen = false;
      });
    };
  }, []);
  const effectiveWorkspaceId = workspaceId || userInfo?.default_workspace;
  const { reportsList, isLoading: isReportsLoading } = useGetLinkedReportDetails(
    patient_uid ? Number(patient_uid) : null,
    linkedResources.reports
  );

  const {
    appointmentsList,
    encountersList,
    isLoading: isLinkedResourcesLoading,
  } = useGetLinkedFhirResources(
    effectiveWorkspaceId,
    patient_uid ? String(patient_uid) : null,
    linkedResources.appointments,
    linkedResources.encounters
  );

  const TitleMaxLength = 50;
  const { data: taskLabelsResponse } = useGetTaskLabels(workspaceId);
  const taskLabels = useMemo(() => taskLabelsResponse || [], [taskLabelsResponse]);

  const tasklabelOptions = useMemo(() => {
    return taskLabels.map((label) => ({
      label: label.name,
      value: label.id,
      color: label.color,
    }));
  }, [taskLabels]);

  // Patient channel can switch `patient_uid` while this form stays mounted; freeze the uid
  // from the first render where we have a patient so save targets the correct patient.
  const patientUidWhenFormOpenedRef = useRef(null);
  if (patientUidWhenFormOpenedRef.current == null && patient_uid) {
    patientUidWhenFormOpenedRef.current = patient_uid;
  }

  // Update reminder state when selectedReminder changes
  useEffect(() => {
    setReminder(() => {
      if (!selectedReminder?.title && !!selectedReminder?.context) {
        const ctx = selectedReminder.context;
        return {
          ...selectedReminder,
          title: ctx.length > 50 ? ctx.slice(0, 47) + "..." : ctx,
        };
      }
      return selectedReminder;
    });
    setLinkedResources(
      selectedReminder?.linked_resources
        ? buildLinkedResourcesPayload(selectedReminder)
        : DEFAULT_LINKED_RESOURCES
    );
  }, [selectedReminder]);

  const isLinkedReourcesEmpty = useMemo(() => {
    return Object.values(linkedResources).every((arr) => arr.length === 0);
  }, [linkedResources]);

  useEffect(() => {
    const reminderId = selectedReminder?.id;
    const effectivePatientUid =
      patientUidWhenFormOpenedRef.current != null
        ? patientUidWhenFormOpenedRef.current
        : patient_uid;

    if (!reminderId || effectivePatientUid == null || effectivePatientUid === "") {
      setReminderNotes([]);
      setNotesNextCursor(null);
      setNotesHasNext(false);
      setIsReminderNotesLoading(false);
      return;
    }

    setIsReminderNotesLoading(true);
    getReminderNotes(effectivePatientUid, reminderId, { page_size: NOTES_PAGE_SIZE })
      .then((data) => {
        setReminderNotes(data?.results ?? []);
        setNotesNextCursor(normalizeNotesCursor(data?.next ?? null));
        setNotesHasNext(Boolean(data?.has_next));
      })
      .catch(() => {
        setReminderNotes([]);
        setNotesNextCursor(null);
        setNotesHasNext(false);
      })
      .finally(() => {
        setIsReminderNotesLoading(false);
      });
  }, [selectedReminder?.id, patient_uid]);

  useEffect(() => {
    setIsNoteComposerOpen(false);
    setNewNoteText("");
  }, [selectedReminder?.id]);

  notesPaginationRef.current = {
    hasNext: notesHasNext,
    nextCursor: notesNextCursor,
    initialLoading: isReminderNotesLoading,
    loadingMore: isReminderNotesLoadingMore,
  };

  // Load when the form body scroller (sibling above the footer) is either shorter
  // than its viewport (no scrollbar) or the user is within `bottomSlack` px of the
  // bottom. The footer is outside this element, so scroll metrics are not affected
  // by the fixed footer bar.
  tryLoadNextNotesRef.current = () => {
    const p = notesPaginationRef.current;
    if (!p.nextCursor || p.initialLoading || p.loadingMore) return;

    const el = reminderFormBodyRef.current;
    const bottomSlack = 96;
    if (el) {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const remaining = scrollHeight - scrollTop - clientHeight;
      const hasOverflow = scrollHeight > clientHeight + 2;
      if (hasOverflow && remaining > bottomSlack) return;
    }

    const reminderId = selectedReminder?.id;
    const effectivePatientUid =
      patientUidWhenFormOpenedRef.current != null
        ? patientUidWhenFormOpenedRef.current
        : patient_uid;
    if (reminderId == null || effectivePatientUid == null || effectivePatientUid === "")
      return;

    setIsReminderNotesLoadingMore(true);
    getReminderNotes(effectivePatientUid, reminderId, {
      page_size: NOTES_PAGE_SIZE,
      cursor: p.nextCursor,
    })
      .then((data) => {
        setReminderNotes((prev) => [...prev, ...(data?.results ?? [])]);
        setNotesNextCursor(normalizeNotesCursor(data?.next ?? null));
        setNotesHasNext(Boolean(data?.has_next));
      })
      .catch(() => {})
      .finally(() => setIsReminderNotesLoadingMore(false));
  };

  useEffect(() => {
    const el = reminderFormBodyRef.current;
    if (!el) return;
    const onScroll = () => tryLoadNextNotesRef.current();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [selectedReminder?.id]);

  useLayoutEffect(() => {
    tryLoadNextNotesRef.current();
  }, [
    reminderNotes.length,
    notesNextCursor,
    isReminderNotesLoading,
    isReminderNotesLoadingMore,
    selectedReminder?.id,
  ]);

  const { data: workspaceData = {} } = useWorkspacePermittedUsers(
    userInfo?.default_workspace
  );

  // Create user options for dropdown
  const taskOwnerOptions = useMemo(() => {
    const users = get(workspaceData, ["users"], []);
    const placeholderOption = { label: t("select_key", "Select"), value: null };
    const userOptions = users
      .map((user) => ({
        label: `${user.name}`,
        value: user.id,
      }))
      .sort((a, b) => compLettersBeforeNumbers(a.label, b.label));
    return [placeholderOption, ...userOptions];
  }, [workspaceData]);

  const onChangeTitle = (val) => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_FIELD_CHANGED, {
      label: "Title",
      value: { value: val, reminderId: reminder?.id },
    });
    setReminder({ ...reminder, title: val });
  };

  const onChangeContext = (val) => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_FIELD_CHANGED, {
      label: "Description",
      value: { value: val, reminderId: reminder?.id },
    });
    setReminder({ ...reminder, context: val });
  };

  const onChangeDate = (val) => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_FIELD_CHANGED, {
      label: "Due date",
      value: {
        value: val ? new Date(val).toDateString() : null,
        reminderId: reminder?.id,
      },
    });
    setReminder({ ...reminder, remind_after: val });
  };

  /** Advance due date from the current reminder value (not “today”). */
  const bumpRemindAfter = (mutate) => {
    const raw = get(reminder, "remind_after", null);
    if (raw == null || raw === "") return;
    const base = new Date(raw instanceof Date ? raw.getTime() : raw);
    if (Number.isNaN(base.getTime())) return;
    const next = mutate(base);
    onChangeDate(next);
  };

  const onChangeTaskOwner = (option) => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_FIELD_CHANGED, {
      label: "Task owner",
      value: { value: option?.value, reminderId: reminder?.id },
    });
    const ownerId = option?.value || null;
    setReminder({ ...reminder, owned_by: ownerId });
  };

  useEffect(() => {
    if (!selectedReminder?.id && userInfo?.details?.account_id) {
      setReminder((prev) => {
        if (prev?.owned_by) return prev;
        return { ...prev, owned_by: userInfo.details.account_id };
      });
    }
  }, [selectedReminder?.id, userInfo?.id]);

  const selectedTaskOwner = useMemo(() => {
    return taskOwnerOptions.find((opt) => opt.value === reminder?.owned_by) || null;
  }, [reminder?.owned_by, taskOwnerOptions]);

  const onChangeLabel = (option) => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_FIELD_CHANGED, {
      label: "Task type",
      value: {
        value: option?.value,
        reminderId: reminder?.id,
      },
    });

    const nextReminder = !option
      ? { ...reminder, labels: [] }
      : {
          ...reminder,
          labels: [
            {
              value: option.value,
              label: option.label,
              color: option.color,
            },
          ],
        };
    setReminder(nextReminder);
  };

  const titleTrimmed = String(get(reminder, "title", "")).trim();
  const remindAfter = get(reminder, "remind_after", null);
  const hasDueDate = remindAfter != null && remindAfter !== "";
  const isPatientContextStale =
    patientUidWhenFormOpenedRef.current != null &&
    patient_uid != null &&
    String(patient_uid) !== String(patientUidWhenFormOpenedRef.current);
  const hasTaskOwner = get(reminder, "owned_by");
  const isSaveDisabled =
    isReminderSubmitInProgress ||
    !titleTrimmed ||
    !hasDueDate ||
    isPatientContextStale ||
    !hasTaskOwner;

  const effectivePatientUidForApi =
    patientUidWhenFormOpenedRef.current != null
      ? patientUidWhenFormOpenedRef.current
      : patient_uid;

  const onSaveReminder = () => {
    if (isSaveDisabled) return;
    const basePayload = {
      context: reminder.context,
      remind_after: reminder.remind_after,
      owned_by: reminder.owned_by || null,
      linked_resources: linkedResources,
      title: titleTrimmed,
      label_ids: buildLabelIds(reminder),
    };
    const payload =
      reminder?.id != null
        ? {
            ...basePayload,
            title: titleTrimmed,
            label_ids: buildLabelIds(reminder),
            linked_resources: linkedResources,
          }
        : basePayload;
    setIsReminderSubmitInProgress(true);
    addEditReminder(payload, effectivePatientUidForApi, reminder?.id)
      .then((res) => {
        trackEvent(
          reminder?.id
            ? MIXPANEL_EVENTS.REMINDER_UPDATED
            : MIXPANEL_EVENTS.REMINDER_SAVED,
          { value: reminder?.id }
        );
        refetchReminders();
        onCloseModal();
      })
      .catch((err) => {
        const rawMessage = err?.response?.data?.message || "";
        const tooLong = rawMessage.match(/at most (\d+) characters/i);
        Toast.error(
          tooLong
            ? t("error.task_description_too_long", {
                count: Number(tooLong[1]),
                defaultValue: "Task description must be at most {{count}} characters.",
              })
            : t("error.task_save_failed", {
                defaultValue: "Couldn't save the task. Please try again.",
              })
        );
      })
      .finally(() => {
        setIsReminderSubmitInProgress(false);
      });
  };

  const isFooterActionBusy =
    isReminderSubmitInProgress || isDeletingReminder || isResolvingReminder;
  const isReminderResolved = Boolean(reminder?.resolved);

  const handleDeleteReminder = () => {
    if (!reminder?.id || isFooterActionBusy) return;
    if (effectivePatientUidForApi == null || effectivePatientUidForApi === "") {
      return;
    }
    setIsDeleteConfirmOpen(false);
    setIsDeletingReminder(true);
    deletePatientReminder(effectivePatientUidForApi, reminder.id)
      .then(() => {
        refetchReminders();
        onCloseModal();
      })
      .catch((err) => {
        console.error("Failed to delete reminder:", err);
      })
      .finally(() => {
        setIsDeletingReminder(false);
      });
  };

  const handleMarkAsDone = () => {
    if (!reminder?.id || isReminderResolved || isFooterActionBusy) return;
    if (effectivePatientUidForApi == null || effectivePatientUidForApi === "") {
      return;
    }
    setIsResolvingReminder(true);
    trackEvent(MIXPANEL_EVENTS.REMINDER_RESOLVED, { value: reminder.id });
    resolveReminder(effectivePatientUidForApi, reminder.id)
      .then(() => {
        refetchReminders();
        onCloseModal();
      })
      .catch((err) => {
        console.error("Failed to resolve reminder:", err);
      })
      .finally(() => {
        setIsResolvingReminder(false);
      });
  };

  const selectedLabel = get(reminder, ["labels", "0"], null);

  const getNoteAuthorDisplay = (note) => {
    const nameFromNote = get(note, ["created_by_info", "name"]);
    if (nameFromNote != null && String(nameFromNote).trim() !== "") {
      return String(nameFromNote).trim();
    }
    return "";
  };

  const openLinkRecordsModal = () => {
    trackEvent(MIXPANEL_EVENTS.REMINDER_LINK_RECORDS_OPENED, {
      value: reminder?.id,
    });
    setIsLinkRecordsOpen(true);
  };

  const closeNoteComposer = () => {
    setIsNoteComposerOpen(false);
    setNewNoteText("");
  };

  const onSaveNewNote = () => {
    const trimmed = newNoteText.trim();
    if (!trimmed || isSavingNote || !reminder?.id) return;

    const effectivePatientUid =
      patientUidWhenFormOpenedRef.current != null
        ? patientUidWhenFormOpenedRef.current
        : patient_uid;
    if (effectivePatientUid == null || effectivePatientUid === "") return;

    setIsSavingNote(true);
    createReminderNote(effectivePatientUid, reminder.id, { note: trimmed })
      .then((created) => {
        if (created && created.id != null) {
          setReminderNotes((prev) => [created, ...prev]);
        } else {
          getReminderNotes(effectivePatientUid, reminder.id, {
            page_size: NOTES_PAGE_SIZE,
          }).then((data) => {
            setReminderNotes(data?.results ?? []);
            setNotesNextCursor(normalizeNotesCursor(data?.next ?? null));
            setNotesHasNext(Boolean(data?.has_next));
          });
        }
        closeNoteComposer();
      })
      .catch(() => {})
      .finally(() => setIsSavingNote(false));
  };

  const noteComposer = isNoteComposerOpen ? (
    <div className="rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-900/80">
      <textarea
        value={newNoteText}
        onChange={(e) => setNewNoteText(e.target.value)}
        placeholder={t("label.reminder_add_comment_placeholder", "Add a comment")}
        rows={4}
        className="w-full min-h-[88px] resize-y bg-gray-100 text-gray-900 p-3 border-0 focus:ring-0 focus:outline-none placeholder:text-gray-500 dark:bg-[#0C1723] dark:text-gray-100 dark:placeholder:text-gray-500 !text-[14px]"
      />
      <div className="flex justify-end items-center gap-5 px-3 py-2.5 bg-gray-100 dark:bg-gray-800/95 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={closeNoteComposer}
          disabled={isSavingNote}
          className="inline-flex items-center gap-1.5 font-medium text-teal-500 hover:text-teal-400 dark:text-teal-400 dark:hover:text-teal-300 disabled:opacity-50 disabled:pointer-events-none text-[14px]"
        >
          <AwesomeIcon name={faXmark} className="h-3.5 w-3.5" />
          {t("button.discard", "Discard")}
        </button>
        <button
          type="button"
          onClick={onSaveNewNote}
          disabled={isSavingNote}
          className="inline-flex items-center gap-1.5 font-medium text-teal-500 hover:text-teal-400 dark:text-teal-400 dark:hover:text-teal-300 disabled:opacity-50 disabled:pointer-events-none text-[14px]"
        >
          <AwesomeIcon name={faCheck} className="h-3.5 w-3.5" />
          {isSavingNote ? t("saving", "Saving…") : t("button.save", "Save")}
        </button>
      </div>
    </div>
  ) : null;

  const renderNoteItem = (n) => {
    const authorDisplay = getNoteAuthorDisplay(n);
    return (
      <li
        key={n.id}
        className="text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700 pb-3 last:border-0 last:pb-0 text-[14px]"
      >
        <div className="whitespace-pre-wrap !break-words">{n.note}</div>
        <div className="text-[12px] text-teal-500 dark:text-teal-400 mt-1.5">
          {n.created_at ? getFormattedDate(n.created_at, "d MMM yyyy") : ""}
          {authorDisplay ? ` · ${authorDisplay}` : ""}
        </div>
      </li>
    );
  };

  // Match stored form state (`value`) and API reminder labels (`id`).
  const selectedLabelId = selectedLabel?.value ?? selectedLabel?.id;
  const selectedTaskLabelOption =
    selectedLabelId != null
      ? tasklabelOptions.find((opt) => opt.value == selectedLabelId) || null
      : null;

  return (
    <>
      <div
        className={classNames(
          "text-left flex min-h-0 flex-1 flex-col bg-white dark:bg-gray-800 rounded-t",
          { "z-50": isAttachmentsViewOpen }
        )}
      >
        <div
          ref={reminderFormBodyRef}
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 [scrollbar-gutter:stable]"
        >
          <Dropdown
            options={tasklabelOptions}
            selectedOption={selectedTaskLabelOption}
            onChange={onChangeLabel}
            isBottomAligned={true}
            parentClassName="text-[14px]"
            className={`bg-gray-100 dark:border-gray-900 dark:bg-gray-900 ${
              !selectedTaskLabelOption
                ? "[&>span:first-child]:italic [&>span:first-child]:text-gray-400 [&>span:first-child]:dark:text-gray-600"
                : ""
            }`}
            placeholder={t("label.select_task_type", "Select task type")}
            iconClassName="w-4 h-4 text-black dark:text-gray-200"
          />

          <div className="flex flex-col divide-y divide-[#E8E9EA] dark:divide-gray-800 rounded-8">
            <TextInput
              value={reminder?.title || ""}
              onChange={(value) => onChangeTitle(value)}
              placeholder={t("label.title", "Title") + "*"}
              className="!rounded-b-none"
              wrapperClassName="!p-0 !border-0 dark:bg-gray-900 "
              inputClassName="!text-[14px] placeholder:!text-[14px] !bg-gray-100 dark:!bg-gray-900 rounded-t-8 placeholder:italic placeholder:text-gray-400 placeholder:dark:text-gray-600"
              maxLength={TitleMaxLength}
              showCharacterCount
            />
            <TextareaInput
              title={""}
              value={reminder?.context || ""}
              onChange={(e) => onChangeContext(e.target.value)}
              showCharacterCount
              isClinicalField
              inputClassName="!text-left !text-[14px] text-gray-800 dark:text-gray-200 !rounded-b-8 placeholder:!text-[14px] sm:!placeholder:text-[14px] border-none placeholder:italic placeholder:text-gray-400 placeholder:dark:text-gray-600"
              placeholder={t("label.reminder_add_description")}
              labelClassName="!text-[14px] text-gray-700 dark:text-gray-300 !m-0"
              className="!w-full dark:!bg-gray-900 !rounded-b-8 !p-0 !rounded-t-none text-[14px]"
              wrapperClassName="!p-0 dark:!bg-gray-900 !rounded-t-none"
              rows={3}
            />
          </div>
          <div className="flex w-full flex-col items-stretch gap-3">
            <div className="flex flex-wrap w-full items-center justify-center gap-3">
              <div className="flex flex-col w-full min-w-[11rem] space-y-3 flex-1">
                <div className="text-md text-gray-400 dark:text-gray-600 uppercase">
                  {t("label.due_date", "Due Date")}
                </div>
                <SingleDateInput
                  tooltip={t("task_due_date") || t("scan_upload_date")}
                  placeholder={t("select_date") + "*"}
                  value={get(reminder, "remind_after", null)}
                  onChange={(val) => onChangeDate(val)}
                  inputFormat="MMM DD, YYYY"
                  classNames="text-[14px] w-full min-w-0"
                  hasBorder={false}
                  bgColor={isDarkMode ? "#0C1723" : undefined}
                  highlightToday
                  options={{
                    size: "xs",
                    withinPortal: true,
                    variant: "unstyled",
                    dropdownPosition: "top-start",
                    zIndex: 10000,
                  }}
                  styles={{
                    ...REMINDER_DUE_DATE_PICKER_STYLES,
                    input: {
                      "::placeholder": {
                        fontStyle: "italic",
                        color: isDarkMode ? "#646B72" : "#9EA3A8",
                      },
                    },
                  }}
                />
              </div>
              <div className="flex flex-col w-full space-y-3 flex-1">
                <div className="text-md text-gray-400 dark:text-gray-600 uppercase">
                  {t("label.task_owner", "Task Owner")}
                </div>
                <SearchableCombobox
                  options={taskOwnerOptions}
                  value={selectedTaskOwner}
                  onChange={onChangeTaskOwner}
                  placeholder={`${t("select_key")}*`}
                  className="bg-gray-100 dark:bg-gray-900"
                  isBottomAligned
                />
              </div>
            </div>
            {hasDueDate ? (
              <div className="mb-3 flex w-full flex-wrap justify-start gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => bumpRemindAfter((d) => addDays(d, 3))}
                  className={DUE_DATE_SHORTCUT_PILL_CLASS}
                >
                  {t("label.due_shortcut_3d", "+ 3d")}
                </button>
                <button
                  type="button"
                  onClick={() => bumpRemindAfter((d) => addWeeks(d, 1))}
                  className={DUE_DATE_SHORTCUT_PILL_CLASS}
                >
                  {t("label.due_shortcut_1w", "+ 1 week")}
                </button>
                <button
                  type="button"
                  onClick={() => bumpRemindAfter((d) => addMonths(d, 1))}
                  className={DUE_DATE_SHORTCUT_PILL_CLASS}
                >
                  {t("label.due_shortcut_1m", "+ 1 month")}
                </button>
                <button
                  type="button"
                  onClick={() => bumpRemindAfter((d) => addYears(d, 1))}
                  className={DUE_DATE_SHORTCUT_PILL_CLASS}
                >
                  {t("label.due_shortcut_1y", "+ 1 year")}
                </button>
              </div>
            ) : null}
          </div>
          {isLinkedReourcesEmpty ? (
            <div className="flex flex-col gap-3">
              <div className="text-md text-gray-400 dark:text-gray-600 uppercase">
                {t("label.link_patient_records", "Link patient records")}
              </div>
              <Button
                className="flex gap-3 items-center justify-center w-full bg-teal-400/15 !text-gray-100 dark:!text-teal-100 hover:bg-teal-400/15 !py-3 !text-[14px]"
                isPrimary
                onClick={openLinkRecordsModal}
              >
                <AwesomeIcon name={faPlus} size="1x" />
                {t("label.select_patient_record", "Select patient record")}
              </Button>
            </div>
          ) : (
            <LinkedRecordsPanel
              linkedResources={linkedResources}
              reports={reportsList}
              appointments={appointmentsList}
              encounters={encountersList}
              onAddRecords={openLinkRecordsModal}
              onViewRecords={() => {
                setIsAttachmentsViewOpen(true);
              }}
              isCreateMode={!reminder?.id}
              isLoading={isReportsLoading || isLinkedResourcesLoading}
            />
          )}
          {reminder?.id ? (
            <div
              className={classNames("flex flex-col gap-3", {
                "rounded-md border border-gray-200 p-3 dark:border-gray-700":
                  reminderNotes.length > 0,
              })}
            >
              <div
                className={classNames("flex items-center justify-between", {
                  "border-b border-gray-200 pb-3 dark:border-gray-700":
                    reminderNotes.length > 0,
                })}
              >
                <span className="text-md text-gray-700 dark:text-gray-400 uppercase">
                  {t("label.comments", "Comments")}
                </span>
                {!(
                  reminderNotes.length === 0 &&
                  !isReminderNotesLoading &&
                  !isNoteComposerOpen
                ) ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => setIsNoteComposerOpen(true)}
                      className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-teal-500 underline hover:text-teal-600 dark:text-teal-400 dark:hover:text-teal-300"
                    >
                      {t("label.add_comment", "Add comment")}
                    </button>
                  </div>
                ) : null}
              </div>
              {noteComposer}
              {isReminderNotesLoading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t("loading", "Loading…")}
                </div>
              ) : reminderNotes.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {reminderNotes.map(renderNoteItem)}
                </ul>
              ) : !isNoteComposerOpen ? (
                <Button
                  type="button"
                  onClick={() => setIsNoteComposerOpen(true)}
                  className="flex w-full gap-3 items-center justify-center bg-teal-400/15 !py-3 !text-gray-100 hover:dark:bg-teal-400/15 dark:!text-teal-100 !text-[14px]"
                  isPrimary
                >
                  <AwesomeIcon name={faPlus} size="1x" />
                  {t("label.add_comment", "Add comment")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="relative z-10 flex w-full min-w-0 shrink-0 items-center gap-x-3 border-t border-gray-200 bg-gray-100 px-4 py-4 dark:border-gray-700 dark:bg-[#0C1723]">
          {reminder?.id ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (!isFooterActionBusy) setIsDeleteConfirmOpen(true);
                }}
                disabled={isFooterActionBusy}
                aria-label={t("button.delete", "Delete")}
                title={t("button.delete", "Delete")}
                className={classNames(
                  "inline-flex shrink-0 items-center justify-center rounded-full border border-transparent text-gray-500 transition-colors hover:text-gray-300 dark:text-gray-600 dark:hover:text-gray-200",
                  {
                    "cursor-not-allowed opacity-50": isFooterActionBusy,
                  }
                )}
              >
                <AwesomeIcon name={faTrash} className="h-[18px] w-[18px] shrink-0" />
              </button>
              <div className="flex min-w-0 flex-1 items-stretch gap-3">
                <button
                  type="button"
                  onClick={handleMarkAsDone}
                  disabled={isReminderResolved || isFooterActionBusy}
                  className={classNames(
                    "inline-flex min-w-0 flex-1 basis-0 items-center justify-center rounded-xl px-3 py-2.5 transition-colors",
                    "bg-gray-200 text-teal-500 hover:bg-teal-900/70 hover:text-gray-100 dark:bg-teal-950/80 dark:text-teal-400 dark:hover:bg-teal-950",
                    {
                      "cursor-not-allowed !bg-gray-300 !text-gray-500 dark:!bg-gray-700 dark:!text-gray-500":
                        isReminderResolved || isFooterActionBusy,
                    }
                  )}
                >
                  {isResolvingReminder
                    ? t("loading", "Loading…")
                    : t("label.mark_as_done", "Mark as done")}
                </button>
                <button
                  type="button"
                  onClick={onSaveReminder}
                  disabled={isSaveDisabled || isDeletingReminder || isResolvingReminder}
                  className={classNames(
                    "inline-flex min-w-0 flex-1 basis-0 items-center justify-center rounded-xl px-3 py-2.5 transition-colors",
                    "bg-teal-700 text-gray-100 hover:bg-teal-800 dark:bg-teal-400 dark:text-gray-900 dark:hover:bg-teal-300",
                    {
                      "cursor-not-allowed !bg-gray-300 !text-gray-500 dark:!bg-gray-700 dark:!text-gray-500":
                        isSaveDisabled || isDeletingReminder || isResolvingReminder,
                    }
                  )}
                >
                  {isReminderSubmitInProgress
                    ? t("message.saving", "Saving…")
                    : t("label.update_and_close", "Update & close")}
                </button>
              </div>
            </>
          ) : (
            <div className="flex w-full flex-1 justify-end">
              <button
                type="button"
                onClick={() => {
                  trackEvent(MIXPANEL_EVENTS.REMINDER_FORM_DISMISSED);
                  onCloseModal();
                }}
                disabled={isReminderSubmitInProgress}
                className="mr-4 flex-1 rounded-md bg-gray-500 px-8 py-3 text-white transition-colors hover:bg-gray-600 disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-600 dark:hover:bg-gray-700"
              >
                {t("button.cancel")}
              </button>
              <button
                type="button"
                onClick={onSaveReminder}
                disabled={isSaveDisabled}
                className={classNames(
                  "flex-1 rounded-md bg-teal-600 px-8 py-3 text-white transition-colors hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-700",
                  {
                    "cursor-not-allowed !bg-gray-300 !text-gray-500 dark:!bg-gray-700 dark:!text-gray-500":
                      isSaveDisabled,
                  }
                )}
              >
                {isReminderSubmitInProgress
                  ? t("message.saving", "Saving…")
                  : t("button.save", "Save")}
              </button>
            </div>
          )}
        </div>
        <LinkPatientRecordsModal
          show={isLinkRecordsOpen}
          onClose={() => setIsLinkRecordsOpen(false)}
          onLink={(linkedResources) => {
            setLinkedResources(linkedResources);
            setIsLinkRecordsOpen(false);
          }}
          linkedRes={linkedResources}
        />
      </div>
      {isAttachmentsViewOpen && (
        <>
          <div className="fixed inset-0 z-[49] bg-black/50" />
          <AttachmentsView
            onCollapse={() => setIsAttachmentsViewOpen(false)}
            linkedResources={linkedResources}
            reports={reportsList}
            appointments={appointmentsList}
            encounters={encountersList}
            patient_uid={patient_uid ? Number(patient_uid) : undefined}
          />
        </>
      )}

      <Modal
        show={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        closeOnClickAway={true}
        zIndex="z-[100]"
        title={
          <span className="text-gray-900 dark:text-gray-100">
            {t("label.delete_task_confirm_title", "Delete task")}
          </span>
        }
        titleClassName="!text-lg !font-semibold"
        className="mx-4 w-full max-w-md rounded-xl bg-white dark:bg-gray-900 sm:mx-0"
      >
        <div className="px-4 pb-4 pt-1">
          <p className="mb-4 pb-4 pt-4 text-left leading-relaxed text-gray-600 dark:text-gray-300  border-b border-gray-200 dark:border-gray-700">
            {t("label.delete_task_confirm_body") ||
              "This action cannot be undone. Are you sure you want to delete this task?"}
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeletingReminder}
              className={classNames(
                "rounded-xl px-4 py-2.5 transition-colors",
                "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
                { "pointer-events-none opacity-50": isDeletingReminder }
              )}
            >
              {t("button.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={handleDeleteReminder}
              disabled={isDeletingReminder}
              className={classNames(
                "rounded-xl px-4 py-2.5 text-white transition-colors",
                "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500",
                { "cursor-not-allowed opacity-70": isDeletingReminder }
              )}
            >
              {isDeletingReminder
                ? t("label.deleting", "Deleting…")
                : t("button.delete", "Delete")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const CreateEditPatientReminderModal = ({
  isOpen,
  onClose,
  onBack,
  patient_uid,
  selectedReminder,
  refetchReminders,
  workspaceId,
}) => {
  const [taskLabelHeaderName, setTaskLabelHeaderName] = useState(() =>
    getTaskLabelDisplayName(selectedReminder, [])
  );

  useEffect(() => {
    if (!isOpen) return;
    setTaskLabelHeaderName(getTaskLabelDisplayName(selectedReminder, []));
  }, [isOpen, selectedReminder]);

  const headerFallback =
    selectedReminder?.id != null
      ? t("label.task", "Task")
      : t("label.add_task", "Add task");
  const headerTitleText =
    taskLabelHeaderName.trim() !== "" ? taskLabelHeaderName : headerFallback;

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={
        <div className="flex w-full min-w-0 items-center gap-2">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="shrink-0 text-xl text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none transition-colors"
              aria-label={t("button.back", "Back")}
            >
              <AwesomeIcon
                name={faChevronLeft}
                size="lg"
                className="text-gray-600 dark:text-gray-300"
              />
            </button>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-left text-gray-800 dark:text-gray-100">
            {headerTitleText}
          </span>
        </div>
      }
      titleClassName="!flex !min-w-0 !flex-1 !items-center !text-lg !font-semibold"
      className="w-full md:w-10/12 lg:w-1/3 bg-gray-50 dark:bg-gray-800 flex min-h-0 flex-col h-full max-h-[60%] sm:max-h-[80%] overflow-hidden"
      closeOnClickAway={true}
    >
      {isOpen ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <CreateEditPatientReminder
            onCloseModal={onClose}
            patient_uid={patient_uid}
            selectedReminder={selectedReminder}
            refetchReminders={refetchReminders}
            workspaceId={workspaceId}
          />
        </div>
      ) : null}
    </Modal>
  );
};
